import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

/**
 * pg-boss v12 job states. See https://github.com/timgit/pg-boss/blob/12.14.0/src/plans.ts
 * for the canonical list. `created`/`retry`/`active` are non-terminal; `completed`,
 * `cancelled`, and `failed` are terminal.
 */
export const JobStateSchema = z.enum([
  'created',
  'retry',
  'active',
  'completed',
  'cancelled',
  'failed',
]);
export type JobState = z.infer<typeof JobStateSchema>;

/**
 * Coerces a postgres.js-returned `Date` to an ISO-8601 string; passes strings through.
 * Used so DB-sourced rows and wire-format rows share one schema.
 */
const IsoTimestampSchema = z.preprocess(v => (v instanceof Date ? v.toISOString() : v), z.string());
const NullableIsoTimestampSchema = z.preprocess(
  v => (v instanceof Date ? v.toISOString() : v),
  z.string().nullable()
);

/**
 * Shape of a pg-boss job row as returned by the getConnectionJobs endpoint.
 * Doubles as a parser for raw DB rows (timestamps may arrive as Date objects)
 * and as the wire contract for API consumers (timestamps are ISO strings).
 */
export const ConnectionJobRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  state: JobStateSchema,
  retryCount: z.number().int(),
  createdOn: IsoTimestampSchema,
  startedOn: NullableIsoTimestampSchema,
  completedOn: NullableIsoTimestampSchema,
  heartbeatOn: NullableIsoTimestampSchema,
  expireSeconds: z.number().int().nullable(),
  policy: z.string().nullable(),
  data: z.unknown().nullable(),
});
export type ConnectionJobRow = z.infer<typeof ConnectionJobRowSchema>;
